// Staff restriction and ban (0309). Reads are the tables under RLS — a hub
// coordinator sees their own hub, admin and Superadmin see all. Every write
// goes through the restrictions function, which passes the verified caller to
// SQL, where who-may-do-what is decided.

import type { SupabaseClient } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

// 0309's tables are not in the generated types until it is pushed.
const db = supabase as unknown as SupabaseClient;

export type RestrictionKind = "standard" | "indefinite" | "banned";
export type RestrictionStatus = "requested" | "approved" | "rejected" | "lifted";

export const KIND_LABEL: Record<RestrictionKind, string> = {
  standard: "Restricted (Standard)",
  indefinite: "Restricted (Indefinite)",
  banned: "Banned",
};

export interface RestrictionRow {
  id: string;
  staffId: string;
  staffName: string;
  staffRole: string;
  kind: RestrictionKind;
  status: RestrictionStatus;
  /** `approved` but past its end date reads as Ended, not In force. */
  inForce: boolean;
  reason: string;
  requestedAt: string;
  decidedAt: string | null;
  endsAt: string | null;
  appeal: { body: string; createdAt: string } | null;
}

async function fetchRestrictions(): Promise<RestrictionRow[]> {
  const [rows, appeals] = await Promise.all([
    db
      .from("staff_restrictions")
      .select(
        "id, staff_id, kind, status, reason, requested_at, decided_at, ends_at, staff:staff_id(full_name, role)",
      )
      .order("requested_at", { ascending: false })
      .limit(500),
    db.from("staff_restriction_appeals").select("staff_id, restriction_id, body, created_at"),
  ]);
  if (rows.error) throw new Error(rows.error.message);
  if (appeals.error) throw new Error(appeals.error.message);
  const byRestriction = new Map(
    (appeals.data ?? []).map((a) => [
      a.restriction_id as string,
      { body: a.body as string, createdAt: a.created_at as string },
    ]),
  );
  const now = Date.now();
  return (rows.data ?? []).map((r) => {
    const staff = (r.staff ?? {}) as { full_name?: string; role?: string };
    const endsAt = (r.ends_at as string | null) ?? null;
    return {
      id: r.id as string,
      staffId: r.staff_id as string,
      staffName: staff.full_name ?? "Unknown",
      staffRole: staff.role ?? "",
      kind: r.kind as RestrictionKind,
      status: r.status as RestrictionStatus,
      inForce: r.status === "approved" && (endsAt === null || Date.parse(endsAt) > now),
      reason: r.reason as string,
      requestedAt: r.requested_at as string,
      decidedAt: (r.decided_at as string | null) ?? null,
      endsAt,
      appeal: byRestriction.get(r.id as string) ?? null,
    };
  });
}

export function useRestrictions() {
  return useQuery({ queryKey: ["restrictions"], queryFn: fetchRestrictions });
}

export function useStandardDuration() {
  return useQuery({
    queryKey: ["restriction-duration"],
    queryFn: async () => {
      const { data, error } = await db
        .from("staff_restriction_settings")
        .select("standard_duration_days")
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data?.standard_duration_days as number | null) ?? null;
    },
  });
}

export interface FlaggedBooking {
  bookingId: string;
  ref: string | null;
  scheduledAt: string;
  status: string;
  staffName: string;
  staffRole: string;
}

export function useFlaggedBookings() {
  return useQuery({
    queryKey: ["restricted-staff-bookings"],
    queryFn: async (): Promise<FlaggedBooking[]> => {
      const { data, error } = await db
        .from("v_restricted_staff_bookings")
        .select("booking_id, ref, scheduled_at, status, staff_name, staff_role")
        .order("scheduled_at");
      if (error) throw new Error(error.message);
      return (data ?? []).map((b) => ({
        bookingId: b.booking_id as string,
        ref: (b.ref as string | null) ?? null,
        scheduledAt: b.scheduled_at as string,
        status: b.status as string,
        staffName: b.staff_name as string,
        staffRole: b.staff_role as string,
      }));
    },
  });
}

/** Therapists and riders the caller may request for (RLS scopes a hub coordinator). */
export function useRestrictableStaff(enabled: boolean) {
  return useQuery({
    queryKey: ["restrictable-staff"],
    enabled,
    queryFn: async () => {
      const { data, error } = await db
        .from("staff")
        .select("id, full_name, role")
        .in("role", ["therapist", "rider"])
        .eq("active", true)
        .order("full_name");
      if (error) throw new Error(error.message);
      return (data ?? []) as { id: string; full_name: string; role: string }[];
    },
  });
}

function useRestrictionAction<I>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: I) => callEdgeFunction<unknown>(path, { data }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["restrictions"] });
      void qc.invalidateQueries({ queryKey: ["restricted-staff-bookings"] });
      void qc.invalidateQueries({ queryKey: ["restriction-duration"] });
    },
  });
}

export const useRequestRestriction = () =>
  useRestrictionAction<{ staffId: string; kind: RestrictionKind; reason: string }>(
    "restrictions/request",
  );
export const useDecideRestriction = () =>
  useRestrictionAction<{ restrictionId: string; approve: boolean; note?: string }>(
    "restrictions/decide",
  );
export const useLiftRestriction = () =>
  useRestrictionAction<{ restrictionId: string; note?: string }>("restrictions/lift");
export const useSetDuration = () => useRestrictionAction<{ days: number }>("restrictions/duration");
